/**
 * Family checkout — insufficient-funds (402 `payment_error`) handling
 * (launch-gap 09).
 *
 * Every new ICA household starts at $0 on a PREPAID wallet and the
 * wallet is the only payment path at checkout, so the FIRST order most
 * parents place hits this path. The server converts the unfunded batch
 * to a payment-pending DRAFT with one automatic retry — 24h out, clamped
 * to the campus ordering cutoff (see `packages/schema/src/managers/carts.ts`
 * `computePaymentRetryAt`, PROD-1299) — so a 402 is a "saved, needs funds"
 * outcome, not a dead end. The 402 body carries the REAL schedule
 * (`paymentRetryAt` / `orderingCutoffAt`) and the copy renders it; it must
 * never promise "within 24 hours" again, because inside the last 24h before
 * cutoff that promise was one the server already knew it could not keep.
 *
 * This module owns the pure logic around that outcome:
 *
 *   - `runFamilyCheckoutPlan` — the submit loop, extracted from
 *     `use-family-checkout` so a 402 on one (date, mealtime) group NO
 *     LONGER aborts the sibling groups (one unfunded day used to strand
 *     the rest of the cart, silently unsubmitted). Non-402 failures
 *     keep the historical abort semantics.
 *   - `buildPaymentPendingNotice` — the action-first copy + the
 *     `returnTo` target for the "Add funds" CTA.
 *
 * Framework-free (no React); the wire submit is injected so the loop
 * is unit-testable — `client/test/lib/family-orders/payment-pending.test.ts`.
 */

import { ApiError } from '@/api/http'
import { type CheckoutGroup, type FamilyCartLine, groupKey } from '@/lib/family-orders/cart'
import { formatCents } from '@/lib/school-orders/money'

// ---------------------------------------------------------------------------
// 402 classification + payload parsing
// ---------------------------------------------------------------------------

/** The family submit path's insufficient-funds rejection. */
export function isPaymentError(error: unknown): error is ApiError {
  return error instanceof ApiError && error.status === 402 && error.code === 'payment_error'
}

export interface PaymentShortfall {
  /** Cents the failed batch needed — null when the body didn't carry it. */
  requiredCents: number | null
  /** Wallet balance at rejection time — null when the body didn't carry it. */
  balanceCents: number | null
  currency: string | null
  /**
   * Whose wallet fell short. 'SPONSOR' = the school's meal-plan account —
   * the parent cannot fix it by adding funds, so the copy must not prompt
   * them to. Null on older payloads.
   */
  payer: 'FAMILY' | 'SPONSOR' | null
  /**
   * When the server will retry the debit automatically (ISO). Null on older
   * payloads or when the campus deadline policy could not be evaluated.
   */
  paymentRetryAt: string | null
  /** The ordering cutoff the retry was clamped against (ISO). Null as above. */
  orderingCutoffAt: string | null
}

/**
 * Read the structured cents off a 402 body (`requiredCents` /
 * `balanceCents` / `currency` — same wire fields as the school path).
 * Absent/malformed fields parse to nulls so the copy falls back to the
 * generic line instead of rendering NaN.
 */
export function parsePaymentShortfall(body: unknown): PaymentShortfall {
  if (body && typeof body === 'object') {
    const { requiredCents, balanceCents, currency, payer, paymentRetryAt, orderingCutoffAt } = body as Partial<{
      requiredCents: number
      balanceCents: number
      currency: string
      payer: string
      paymentRetryAt: string
      orderingCutoffAt: string
    }>
    return {
      requiredCents: typeof requiredCents === 'number' && Number.isFinite(requiredCents) ? requiredCents : null,
      balanceCents: typeof balanceCents === 'number' && Number.isFinite(balanceCents) ? balanceCents : null,
      currency: typeof currency === 'string' && currency.length > 0 ? currency : null,
      payer: payer === 'FAMILY' || payer === 'SPONSOR' ? payer : null,
      paymentRetryAt: isoInstantOrNull(paymentRetryAt),
      orderingCutoffAt: isoInstantOrNull(orderingCutoffAt),
    }
  }
  return {
    requiredCents: null,
    balanceCents: null,
    currency: null,
    payer: null,
    paymentRetryAt: null,
    orderingCutoffAt: null,
  }
}

function isoInstantOrNull(value: unknown): string | null {
  return typeof value === 'string' && Number.isFinite(Date.parse(value)) ? value : null
}

// ---------------------------------------------------------------------------
// The automatic-retry clause (PROD-1299)
// ---------------------------------------------------------------------------

/** The two instants the retry copy is built from — the 402 body or an order row. */
export interface PaymentRetrySchedule {
  paymentRetryAt: string | null
  /** The ordering cutoff; the order row exposes it as `deadlineAt`. */
  orderingCutoffAt: string | null
}

/**
 * One sentence describing what happens if the parent does NOT add funds —
 * built from the schedule the server actually stamped, never from a
 * hardcoded "24 hours".
 *
 *   - retry strictly before the cutoff → "we'll retry the payment once
 *     automatically <when>, then cancel the unpaid order";
 *   - retry AT/after the cutoff (the server's "cutoff already inside the
 *     safety margin" case) → "add funds before <cutoff> or this order won't
 *     be placed" — no retry is promised because none can help;
 *   - several pending orders with different schedules → the generic
 *     "before each day's ordering cutoff" line;
 *   - nothing scheduled / unknown → null (caller renders no clause).
 *
 * `timeZone` is the campus zone when the caller knows it (the day view);
 * the checkout dialog falls back to the browser's zone.
 */
export function describeAutomaticRetry(
  schedules: readonly PaymentRetrySchedule[],
  now: Date = new Date(),
  timeZone?: string,
): string | null {
  const scheduled = schedules.filter(
    (s): s is PaymentRetrySchedule & { paymentRetryAt: string } => typeof s.paymentRetryAt === 'string',
  )
  if (scheduled.length === 0) {
    return null
  }
  const distinct = new Set(scheduled.map((s) => `${s.paymentRetryAt}|${s.orderingCutoffAt ?? ''}`))
  if (distinct.size > 1) {
    return 'Otherwise we’ll retry the payment once automatically before each day’s ordering cutoff, then cancel any order that is still unpaid.'
  }
  const [first] = scheduled
  const retryAt = new Date(first!.paymentRetryAt)
  const cutoffAt = first!.orderingCutoffAt ? new Date(first!.orderingCutoffAt) : null
  if (cutoffAt && retryAt.getTime() >= cutoffAt.getTime()) {
    return `Add funds before ${formatMoment(cutoffAt, now, timeZone)} or this order won’t be placed — ordering closes then and it can’t be retried afterwards.`
  }
  return `Otherwise we’ll retry the payment once automatically ${formatMoment(retryAt, now, timeZone)}, then cancel the unpaid order if it still can’t be completed.`
}

/** "today at 9:27 PM" / "tomorrow at 9:27 PM" / "Fri, Sep 4 at 9:27 PM", in `timeZone`. */
export function formatMoment(at: Date, now: Date, timeZone?: string): string {
  const zone = timeZone ? { timeZone } : {}
  const dayKey = (d: Date) => new Intl.DateTimeFormat('en-CA', { ...zone, dateStyle: 'short' }).format(d)
  const time = new Intl.DateTimeFormat(undefined, { ...zone, hour: 'numeric', minute: '2-digit' }).format(at)
  const todayKey = dayKey(now)
  const atKey = dayKey(at)
  if (atKey === todayKey) {
    return `today at ${time}`
  }
  if (atKey === dayKey(new Date(now.getTime() + 86_400_000))) {
    return `tomorrow at ${time}`
  }
  const day = new Intl.DateTimeFormat(undefined, { ...zone, weekday: 'short', month: 'short', day: 'numeric' }).format(
    at,
  )
  return `${day} at ${time}`
}

/**
 * One human line naming the amounts when a shortfall carries them
 * ("This order needs $12.50, but your balance is $0.00."), else null so
 * the caller keeps its generic copy. The raw ledger message ("Account
 * cxy… has 0 USD balance…") must never reach the screen.
 */
export function paymentShortfallLine(
  shortfall: Pick<PaymentShortfall, 'requiredCents' | 'balanceCents' | 'currency' | 'payer'>,
): string | null {
  if (shortfall.payer === 'SPONSOR') {
    // The school's meal-plan wallet fell short — the parent wasn't charged
    // and adding funds to THEIR wallet cannot fix it.
    return "Your school's meal-plan balance couldn't cover its share of this order. You weren't charged — please contact your school."
  }
  if (shortfall.requiredCents === null || shortfall.balanceCents === null) {
    return null
  }
  const currency = shortfall.currency ?? undefined
  return `This order needs ${formatCents(shortfall.requiredCents, currency)}, but your balance is ${formatCents(shortfall.balanceCents, currency)}.`
}

// ---------------------------------------------------------------------------
// The submit loop
// ---------------------------------------------------------------------------

/** The slice of `FamilyOrderLocation` the loop needs (kept structural for tests). */
export interface CheckoutPlanLocation {
  id: string
  organizationId: string
  children: ReadonlyArray<{ userId: string; accountId: string }>
}

export interface CheckoutPlan {
  location: CheckoutPlanLocation
  groups: CheckoutGroup[]
}

/** One per-household-account submit within a (date, mealtime) group. */
export interface SubmitBatchArgs {
  accountId: string
  location: CheckoutPlanLocation
  group: CheckoutGroup
  lines: FamilyCartLine[]
}

export interface GroupOutcome {
  locationId: string
  date: string
  mealtime: CheckoutGroup['mealtime']
  groupKey: string
  /**
   * `submitted` — every account batch in the group went through.
   * `payment_pending` — at least one account batch 402'd; the server
   * saved it as a payment-pending DRAFT (nothing charged, one automatic
   * retry before the ordering cutoff), so the lines leave the cart either way.
   */
  outcome: 'submitted' | 'payment_pending'
  /** Parsed 402 payloads, one per unfunded account batch in the group. */
  shortfalls: PaymentShortfall[]
}

export interface RunCheckoutPlanResult {
  /** Groups that fully reached the server (submitted or payment-pending). */
  outcomes: GroupOutcome[]
  /**
   * Non-402 error that stopped the run (409 price_changed, 422
   * price_missing, network, …) — the remaining groups were NOT
   * attempted and their lines stay in the cart. Null when the whole
   * plan was processed.
   */
  aborted: unknown | null
}

/**
 * Submit every (campus, date, mealtime) group, one batch per household
 * account within the group.
 *
 * Contract (encoded in the unit tests):
 *   - a 402 `payment_error` records the group as `payment_pending` and
 *     the loop CONTINUES — later days/campuses still submit;
 *   - any other error aborts the run (mirrors the pre-existing
 *     price-change/price-missing semantics — those need a consent
 *     dialog before anything else is placed);
 *   - a group where one account batch 402'd and a sibling account batch
 *     submitted is `payment_pending` (its cart lines are consumed
 *     either way — the pending half lives server-side).
 */
export async function runFamilyCheckoutPlan(
  plans: readonly CheckoutPlan[],
  submitBatch: (args: SubmitBatchArgs) => Promise<unknown>,
): Promise<RunCheckoutPlanResult> {
  const outcomes: GroupOutcome[] = []
  for (const { location, groups } of plans) {
    const accountByStudent = new Map(location.children.map((child) => [child.userId, child.accountId]))
    for (const group of groups) {
      // Each line bills the household ITS student belongs to — one
      // batch per household account within the (date, mealtime) group.
      const linesByAccount = new Map<string, FamilyCartLine[]>()
      for (const line of group.lines) {
        const accountId = accountByStudent.get(line.studentUserId)
        if (!accountId) {
          return { outcomes, aborted: new Error('No household billing account is available for this student.') }
        }
        const bucket = linesByAccount.get(accountId) ?? []
        bucket.push(line)
        linesByAccount.set(accountId, bucket)
      }
      const shortfalls: PaymentShortfall[] = []
      for (const [accountId, accountLines] of linesByAccount) {
        try {
          await submitBatch({ accountId, location, group, lines: accountLines })
        } catch (error) {
          if (isPaymentError(error)) {
            // The server SAVED this batch as a payment-pending DRAFT —
            // record the shortfall and keep going; one unfunded day
            // must not strand the rest of the cart.
            shortfalls.push(parsePaymentShortfall(error.body))
            continue
          }
          if (shortfalls.length > 0) {
            // Part of this group already reached the server as pending —
            // report the group so its lines leave the cart, then abort.
            outcomes.push(pendingOutcome(location.id, group, shortfalls))
          }
          return { outcomes, aborted: error }
        }
      }
      outcomes.push(
        shortfalls.length > 0
          ? pendingOutcome(location.id, group, shortfalls)
          : {
              locationId: location.id,
              date: group.date,
              mealtime: group.mealtime,
              groupKey: groupKey(group.date, group.mealtime),
              outcome: 'submitted',
              shortfalls: [],
            },
      )
    }
  }
  return { outcomes, aborted: null }
}

function pendingOutcome(locationId: string, group: CheckoutGroup, shortfalls: PaymentShortfall[]): GroupOutcome {
  return {
    locationId,
    date: group.date,
    mealtime: group.mealtime,
    groupKey: groupKey(group.date, group.mealtime),
    outcome: 'payment_pending',
    shortfalls,
  }
}

// ---------------------------------------------------------------------------
// The action-first notice
// ---------------------------------------------------------------------------

export interface PaymentPendingNotice {
  title: string
  /** What happened + what's saved, one short paragraph. */
  description: string
  /** Named amounts, when every unfunded batch carried them. Else null. */
  amountLine: string | null
  /** Reassurance + the two paths out (add funds now / automatic retry). */
  nextSteps: string
  /**
   * Where "Add funds" should RETURN the parent to: the pending order's
   * day view when exactly one day is pending, else the weekly orders
   * view (the checkout page would greet them with an empty cart — the
   * pending order lives on the orders views).
   */
  returnTo: string
}

/**
 * Build the insufficient-funds notice off the run outcomes. Null when
 * nothing is payment-pending (the caller shows its normal success
 * path).
 */
export function buildPaymentPendingNotice(
  outcomes: readonly GroupOutcome[],
  now: Date = new Date(),
): PaymentPendingNotice | null {
  const pending = outcomes.filter((o) => o.outcome === 'payment_pending')
  if (pending.length === 0) {
    return null
  }
  const submittedCount = outcomes.length - pending.length

  // Named amounts only when they aggregate honestly: every unfunded
  // batch carries cents, one currency, one balance figure (i.e. one
  // household account) — then "needs" is the sum across batches.
  const shortfalls = pending.flatMap((o) => o.shortfalls)
  let amountLine: string | null = null
  const first = shortfalls[0]
  if (
    first &&
    first.requiredCents !== null &&
    first.balanceCents !== null &&
    shortfalls.every(
      (s) =>
        s.requiredCents !== null &&
        s.balanceCents === first.balanceCents &&
        (s.currency ?? null) === (first.currency ?? null),
    )
  ) {
    const requiredTotal = shortfalls.reduce((sum, s) => sum + (s.requiredCents ?? 0), 0)
    amountLine = paymentShortfallLine({
      requiredCents: requiredTotal,
      balanceCents: first.balanceCents,
      currency: first.currency,
      payer: first.payer ?? null,
    })
  }

  const pendingDates = [...new Set(pending.map((o) => o.date))].sort()
  const onlyDate = pendingDates.length === 1 ? pendingDates[0] : null

  // The retry clause comes from the schedule the server stamped (PROD-1299).
  // Adding funds completes the order on its own now — the wallet credit
  // settles payment-pending orders — so the first sentence no longer hinges
  // on the parent finding the "complete it now" button.
  const retryClause = describeAutomaticRetry(shortfalls, now)
  return {
    title: 'Add funds to complete your order',
    description:
      submittedCount > 0
        ? 'Your wallet balance didn’t cover part of this order. The rest of your meals are placed; the unpaid part is saved and nothing was charged.'
        : 'Your wallet balance didn’t cover this order. It’s saved and nothing was charged.',
    amountLine,
    nextSteps: retryClause
      ? `Add funds and your order completes automatically. ${retryClause}`
      : 'Add funds and your order completes automatically. If you skip it, the unpaid order is cancelled when ordering closes for that day.',
    returnTo: onlyDate ? `/orders/family/day?date=${encodeURIComponent(onlyDate)}` : '/orders/family',
  }
}
